import React, { useMemo, useCallback, useContext } from 'react';
import { TreeSelect, ConfigProvider } from 'antd';
import type { TreeSelectProps as AntTreeSelectProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { useFieldValidation } from '../../base/useFieldValidation';
import { mergeStoreProps } from '../../base/storePropsUtils';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';
import { DataSetInheritContext } from '../../base/DataSetInheritContext';

/**
 * TreeSelect Semantic DOM 样式配置
 * 参考 Ant Design 6.x Semantic DOM 规范
 */
export interface TreeSelectSemanticStyles {
  root?: React.CSSProperties;
  prefix?: React.CSSProperties;
  placeholder?: React.CSSProperties;
  input?: React.CSSProperties;
  suffix?: React.CSSProperties;
  popup?: {
    root?: React.CSSProperties;
    item?: React.CSSProperties;
    itemTitle?: React.CSSProperties;
  };
}

export interface TreeSelectSemanticClassNames {
  root?: string;
  prefix?: string;
  placeholder?: string;
  input?: string;
  suffix?: string;
  popup?: {
    root?: string;
    item?: string;
    itemTitle?: string;
  };
}

/**
 * TreeSelect Design Token
 * 参考 Ant Design 6.x Design Token 规范
 */
export interface TreeSelectToken {
  // 官方 Design Token
  controlItemWidth?: string | number;
  controlWidth?: string | number;
  dropdownHeight?: string | number;
  menuPadding?: string | number;
  optionPadding?: string | number;
  optionSelectedBg?: string;
  optionSelectedColor?: string;
  optionSelectedFontWeight?: string | number;
}

/**
 * 校验规则接口
 */
export interface ValidationRule {
  required?: boolean;
  message?: string;
}

export interface AndTreeSelectProps extends Omit<AntTreeSelectProps, 'onChange'>, BaseComponentProps, DataSetConfig, ValidationDisplayConfig {
  changeAction?: string;
  onChange?: (value: any, labelList?: React.ReactNode[], extra?: any) => void;
  remoteUrl?: string;
  selectAction?: string;
  
  // Semantic DOM 支持
  styles?: TreeSelectSemanticStyles;
  classNames?: TreeSelectSemanticClassNames;
  
  // Design Token 支持
  token?: TreeSelectToken;
  
  // 校验相关
  rules?: ValidationRule[];
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
}

export const AndTreeSelect: React.FC<AndTreeSelectProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  changeAction,
  onChange,
  value: propValue,
  defaultValue,
  treeData,
  remoteUrl: _remoteUrl,
  selectAction: _selectAction,
  styles,
  classNames,
  token,
  rules,
  validateOnChange,
  validateOnBlur,
  showValidateHelp = true,
  ...restProps
}) => {
  const ctx = useContext(DataSetInheritContext);
  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );
  const storeProps = useAndStore((s) =>
    sessionId && id ? s.propsStore[`${sessionId}_props`]?.[id] ?? null : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && dataSetField ? s.getDataSet(dataSetKey, dataSetField) : undefined
  );

  const { validate, help } = useFieldValidation({
    rules,
    dataSet,
    dataSetField,
    sessionId,
    id,
    develop,
  });

  const displayValue = useMemo(
    () => storeValue ?? storeProps?.value ?? propValue ?? defaultValue,
    [storeValue, storeProps?.value, propValue, defaultValue]
  );

  const mergedProps = useMemo(
    () => mergeStoreProps(restProps as Record<string, any>, storeProps ?? undefined),
    [restProps, storeProps]
  );

  const treeSelectDomProps = useMemo(() => {
    const merged = {
      ...(mergedProps as Record<string, unknown>),
      ...(styles ? { styles } : {}),
    };
    return prepareAntdDomProps(merged);
  }, [mergedProps, styles]);

  const handleChange = useCallback(
    (value: any, labelList?: React.ReactNode[], extra?: any) => {
      if (onChange) {
        onChange(value, labelList, extra);
        return;
      }
      if (dataSetKey && dataSetField) setDataSet(dataSetKey, dataSetField, value);
      if (validateOnChange) validate(value);
      if (changeAction && sessionId) {
        const { actionName, arg } = parseChangeAction(changeAction, value, {
          id,
          dataSet,
          dataSetField,
        }, ctx);
        addCommand(sessionId, createCommand(actionName, arg));
      }
    },
    [onChange, dataSetKey, dataSetField, setDataSet, validateOnChange, validate, changeAction, sessionId, id, addCommand, ctx]
  );

  const handleBlur = useCallback(() => {
    if (validateOnBlur) validate(displayValue);
  }, [validateOnBlur, validate, displayValue]);

  // 过滤空值的 token
  const filteredToken = useMemo(() => {
    if (!token) return undefined;
    const result: Record<string, any> = {};
    Object.entries(token).forEach(([key, value]) => {
      if (value !== '' && value !== undefined && value !== null) {
        result[key] = value;
      }
    });
    return Object.keys(result).length > 0 ? result : undefined;
  }, [token]);

  // 构建 theme 配置
  const themeConfig = useMemo(() => {
    if (!filteredToken) return undefined;
    return {
      components: {
        TreeSelect: filteredToken,
      },
    };
  }, [filteredToken]);

  const status = help ? 'error' : undefined;

  const treeSelectElement = (
    <div onBlur={handleBlur}>
      <TreeSelect
        {...treeSelectDomProps}
        disabled={develop ? false : (treeSelectDomProps.disabled as boolean | undefined)}
        open={develop ? false : undefined}
        treeData={treeData}
        value={displayValue}
        onChange={handleChange}
        style={{ width: '100%', ...(treeSelectDomProps.style as React.CSSProperties) }}
        classNames={classNames}
        status={status}
      />
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );

  if (themeConfig) {
    return <ConfigProvider theme={themeConfig}>{treeSelectElement}</ConfigProvider>;
  }
  return treeSelectElement;
};

export default AndTreeSelect;
